import { useEffect, useRef, useState } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

const problems = [
  {
    icon: 'ri-search-eye-line',
    title: 'Zákazníci vás na Googli nenájdu',
    text: 'Ak váš web nie je viditeľný, existujete len pre tých, čo vás už poznajú. Každý deň bez webu je zákazník, ktorý odišiel ku konkurencii.',
    stat: '93%',
    statLabel: 'zákazníkov hľadá online pred nákupom',
  },
  {
    icon: 'ri-speed-line',
    title: 'Starý web odoháňa namiesto aby lákal',
    text: 'Pomalý, zastaraný alebo neprehľadný web pôsobí nedôveryhodne. Väčšina návštevníkov odíde do 3 sekúnd — bez šance na návrat.',
    stat: '3 sek',
    statLabel: 'a zákazník odíde, ak web nestihne načítať',
  },
  {
    icon: 'ri-smartphone-line',
    title: 'Na mobile nevyzerá dobre',
    text: 'Viac ako 60% zákazníkov navštívi váš web na mobile. Ak tam nevyzerá dobre, stratili ste ich — a oni to vedia.',
    stat: '60%+',
    statLabel: 'návštev prichádza z mobilných zariadení',
  },
];

export default function TWProblems() {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-zinc-950">
      <div className="max-w-6xl mx-auto px-6">
        <div ref={ref}>
          <div
            className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-error-warning-line text-red-400 text-sm" />
              <span className="text-red-400 text-sm font-semibold">Prečo to nefunguje</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4 leading-tight">
              Bez dobrého webu strácate zákazníkov každý deň
            </h2>
            <p className="text-zinc-400 text-base leading-relaxed">
              Tieto problémy riešia stovky slovenských firiem. A každý z nich má konkrétne riešenie.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {problems.map((item, i) => (
              <div
                key={item.title}
                className={`bg-zinc-900/60 border border-zinc-800 rounded-2xl p-7 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-red-500/10 border border-red-500/20 mb-5">
                  <i className={`${item.icon} text-red-400 text-xl`} />
                </div>
                <h3 className="text-white font-bold text-lg mb-3 leading-tight">{item.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed mb-6">{item.text}</p>
                <div className="border-t border-zinc-800 pt-5">
                  <div className="text-2xl font-black text-red-400 mb-1">{item.stat}</div>
                  <div className="text-zinc-600 text-xs">{item.statLabel}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
